import { type FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TableRow } from '@/components/ui/table';
import { getProjectEstates, type ProjectEstates } from '@/features/projects/api/project-estates';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import type { SortByValues } from '@/utils/sortTypes';

interface EstatesInfiniteScrollProps {
  projectId: string;
  filters?: object;
  sort?: {
    by?: SortByValues;
    order?: 'asc' | 'desc';
  };
  navigateToEstatePage: (estateId: string) => void;
  rowRenderer: (estate: ProjectEstates[number]) => JSX.Element;
}

interface EstatesState {
  [key: string]: ProjectEstates[number];
}

export const ProjectEstatesInfiniteScroll: FC<EstatesInfiniteScrollProps> = ({
  filters,
  projectId,
  sort,
  rowRenderer,
  navigateToEstatePage,
}) => {
  const [loading, setLoading] = useState(false);
  const [stopLoad, setStopLoad] = useState(false);
  const [estates, setEstates] = useState<EstatesState | null>(null);
  const querySignature = useMemo(
    () => JSON.stringify({ filters, projectId, sort }),
    [filters, projectId, sort]
  );
  const activeQuerySignatureRef = useRef(querySignature);

  const { lastElementRef, pageNum, resetPageNum } = useInfiniteScroll<HTMLTableRowElement>({
    loading,
    stopLoad,
    startPage: 1,
  });

  const loadMoreEstates = useCallback(async () => {
    if (pageNum <= 1) return;
    if (activeQuerySignatureRef.current !== querySignature) return;

    setLoading(true);
    try {
      const { estates } = await getProjectEstates({
        projectId,
        query: {
          page: pageNum,
          ...(filters && Object.keys(filters).length && { filters }),
          ...(sort?.by &&
            sort.order && {
              sort: {
                by: sort.by,
                order: sort.order,
              },
            }),
        },
      });
      const estatesPage = estates.data;
      if (activeQuerySignatureRef.current !== querySignature) return;
      if (!estatesPage.length) return setStopLoad(true);

      const estatesCollection = estatesPage.reduce<Record<string, ProjectEstates[number]>>(
        (acc, v) => {
          acc[v.id] = v;
          return acc;
        },
        {}
      );
      setEstates((prev) =>
        prev
          ? {
              ...prev,
              ...estatesCollection,
            }
          : estatesCollection
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, filters, pageNum, querySignature, sort]);

  useEffect(() => {
    loadMoreEstates();
  }, [loadMoreEstates]);

  useEffect(() => {
    activeQuerySignatureRef.current = querySignature;
    setEstates(null);
    resetPageNum();
    setStopLoad(false);
  }, [querySignature, resetPageNum]);

  return (
    <Fragment>
      {estates
        ? Object.values(estates).map((estate) => {
            return (
              <TableRow
                key={estate.id}
                className="cursor-pointer items-center text-primary-dark"
                onClick={() => navigateToEstatePage(estate.id)}
              >
                {rowRenderer(estate)}
              </TableRow>
            );
          })
        : null}
      {!stopLoad ? <TableRow ref={lastElementRef} className="h-4 p-0" /> : null}
    </Fragment>
  );
};
